/**
 * Custom Login Form Component
 * Replaces Amplify Authenticator
 */

import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Link
} from '@mui/material';
import { signIn } from '../../services/auth/authService';

export default function LoginForm({ onSuccess, onForgotPassword, onSignUp, onMfaRequired, onNewPasswordRequired }) {
  const [accountId, setAccountId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Read installation payment model - hide sign-up for CONTRACT installations
  const paymentModel = import.meta.env.VITE_PAYMENT_MODEL;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Strip spaces from Account ID before sending to backend
      const cleanAccountId = accountId.replace(/\s+/g, '');
      const result = await signIn(cleanAccountId, email, password);
      console.log('[LoginForm] Sign in result:', result);

      // Handle challenges
      if (result.challengeName) {
        console.log('[LoginForm] Challenge detected:', result.challengeName);

        if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
          if (onNewPasswordRequired) {
            onNewPasswordRequired(result, accountId); // Pass Account ID, not email
          } else {
            setError('New password required but not configured');
          }
          return;
        }

        if (result.challengeName === 'SMS_MFA' || result.challengeName === 'SOFTWARE_TOKEN_MFA') {
          if (onMfaRequired) {
            onMfaRequired(result);
          } else {
            setError('MFA is required but not configured');
          }
          return;
        }

        setError(`Unsupported challenge: ${result.challengeName}`);
        return;
      }

      // Success - tokens stored automatically by authService
      if (onSuccess) {
        onSuccess(result.user);
      }
    } catch (err) {
      console.error('Login error:', err);

      // Extract error message from Auth API response or Cognito error
      const errorMessage = err.response?.data?.error || err.message;

      // User-friendly error messages
      if (errorMessage.includes('UserNotFoundException') || errorMessage.includes('Account not found')) {
        setError('Account not found. Please check your Account ID or email.');
      } else if (errorMessage.includes('NotAuthorizedException') || errorMessage.includes('Incorrect Account ID')) {
        setError('Incorrect Account ID, email, or password.');
      } else if (errorMessage.includes('UserNotConfirmedException') || errorMessage.includes('not verified')) {
        setError('Please verify your email before signing in.');
      } else if (errorMessage.includes('Account locked') || errorMessage.includes('Account temporarily locked')) {
        setError(errorMessage); // Show full lockout message with duration
      } else if (errorMessage.includes('Invalid email for this Account ID')) {
        setError('The email does not match this Account ID.');
      } else {
        setError(errorMessage || 'Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 3
      }}
    >
      <Typography variant="h5" component="h1" gutterBottom>
        Sign In
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Account ID"
        value={accountId}
        onChange={(e) => setAccountId(e.target.value)}
        disabled={loading}
        required
        placeholder="1234 5678 9012"
        helperText="12-digit Account ID (sent to your email when you signed up)"
        inputProps={{
          maxLength: 14,  // Allow spaces: "1234 5678 9012" (12 digits + 2 spaces)
          pattern: '[0-9 ]+',
          inputMode: 'numeric'
        }}
        autoFocus
      />

      <TextField
        fullWidth
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={loading}
        required
        autoComplete="email"
      />

      <TextField
        fullWidth
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={loading}
        required
        autoComplete="current-password"
      />

      <Button
        fullWidth
        type="submit"
        variant="contained"
        size="large"
        disabled={loading}
        sx={{ mt: 1, bgcolor: '#2d3e50', '&:hover': { bgcolor: '#3d4e60' } }}
      >
        {loading ? <CircularProgress size={24} /> : 'Sign In'}
      </Button>

      <Box sx={{ display: 'flex', justifyContent: paymentModel === 'CONTRACT' ? 'flex-start' : 'space-between', mt: 1 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={onForgotPassword}
          disabled={loading}
        >
          Forgot Password?
        </Link>

        {paymentModel !== 'CONTRACT' && (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={onSignUp}
            disabled={loading}
          >
            Create Account
          </Link>
        )}
      </Box>
    </Box>
  );
}
